import { parsePhoneNumber } from 'libphonenumber-js';
import truncate from 'lodash/fp/truncate';

export const mailRegex =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
export const stringRegex = /^[a-zA-Z-' éèàçùîïôöäë]+$/u; // allow - and '

/** Contenu unique de la classe de caractères autorisés pour {@link nameRegex} et {@link sanitizeTalentNameField}. */
const TALENT_NAME_ALLOWED_CHAR_CLASS_SOURCE = "\\p{Script=Latin}\\p{Mark}\\s'’\\-";

export const nameRegex = new RegExp(`^(?!.*[()[\\].])[${TALENT_NAME_ALLOWED_CHAR_CLASS_SOURCE}]+$`, 'u');
export const emojiRegex = /(\p{Emoji_Presentation}|\p{Emoji}\uFE0F)/gu;

export const formatSpace = (string: string): string => {
  return string.replaceAll(' ', '');
};

export const sanitize = (string: string) => {
  return string?.replaceAll(/[-[\]{}()\\/*@'§`£+?\\_.=:,!&;\\^$|%#\s]/g, '');
};

/** Derives up to 2 uppercase initials from a full name, for avatar/initial-badge display. */
export const initialsFromName = (name: string): string =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('') || '?';

export const capitalize = (s: string): string => {
  const arrayOfS = s.split(' ');
  const capitalized = arrayOfS.map((string: string) => {
    return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
  });
  return capitalized.join(' ');
};

export const removePointsAndSpaces = (value: string) => {
  return value.replace(/[.\s]/g, '');
};

export const validatePhoneForE164Regex = '^\\+[1-9]\\d{10,14}$';

export const validateEmail = (email: string): boolean => {
  if (email.toLocaleLowerCase().match(mailRegex)) {
    return true;
  } else {
    return false;
  }
};

export const validatePhone = (phoneNumber: string) => {
  const regex = /(^\+[0-9]{2}|^\+[0-9]{2}\(0\)|^\(\+[0-9]{2}\)\(0\)|^00[0-9]{2}|^0)([0-9]{9}$|[0-9\-\s]{10}$)/;
  if (phoneNumber.match(regex)) {
    return true;
  } else {
    return false;
  }
};

export const trimAndClearInvisibleCharacters = (text: string): string => {
  return text.trim().replace(/[\u200B-\u200D\uFEFF\u200E\u200F\u202A-\u202E]/g, '');
};

const talentNameDisallowedForStrip = new RegExp(`[^${TALENT_NAME_ALLOWED_CHAR_CLASS_SOURCE}]`, 'gu');

/**
 * Retire émojis et caractères non autorisés par {@link nameRegex} (même politique que les communications sortantes).
 * À utiliser avant persistance talent ; si le résultat est vide, traiter comme nom/prénom manquant côté métier.
 */
export const sanitizeTalentNameField = (value: string): string => {
  return trimAndClearInvisibleCharacters(value)
    .replace(emojiRegex, '')
    .replace(talentNameDisallowedForStrip, '')
    .replace(/\s+/g, ' ')
    .trim();
};

export const normalizeEmail = (email?: string): string | undefined => {
  return email?.trim().toLowerCase();
};

export const unaccent = (string: string) => {
  if (!string) return '';
  return string?.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
};

export const formatPhoneForE164 = (phoneNumber: string, whatsapp: boolean): string => {
  let number: string = phoneNumber?.replace(/\s/g, '').replaceAll('.', '');
  try {
    if (phoneNumber?.includes('+')) {
      number = parsePhoneNumber(number)?.format('E.164') as string;
    } else {
      number = parsePhoneNumber(number, 'FR')?.format('E.164') as string;
    }
  } catch (e: unknown) {
    const error = e as Error;
    throw new Error(`bad phone number format: ${error.message}`, { cause: error });
  }

  return whatsapp ? `whatsapp:${number}` : number;
};

export const truncateString = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  return truncate({ length: maxLength, omission: '...' }, text);
};

/** Convertit les sauts de ligne en balises HTML pour un affichage correct. */
export const newlinesToBr = (text: string): string => text.replace(/\n/g, '<br/>');

export const stringToEnumType = <T = unknown>(enumValue: string, typeOfEnum: any) => {
  const enumType = typeOfEnum[enumValue as keyof typeof typeOfEnum];
  if (enumType === undefined) {
    throw new Error('cant cast ' + enumValue);
  }
  if (typeof enumType === 'number') {
    throw new Error('dont cast with ' + enumValue);
  }
  return enumType as T;
};
